// src/pages/OrderPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { getStorageItem, setStorageItem } from '../utils/storage';
import ApiService from '../services/ApiService';
import NotificationUtils from '../utils/NotificationUtils';
import '../styles/order.css';

const OrderPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('en_cours');
  const [notificationPermission, setNotificationPermission] = useState(
    NotificationUtils.isSupported() ? Notification.permission : 'denied'
  );

  // Mappage des statuts de commande
  const statusMap = {
    en_cours: { 
      label: 'En cours de traitement', 
      subtitle: 'Votre commande a été reçue et est en cours de traitement',
      progress: 0
    },
    validee: { 
      label: 'Validée', 
      subtitle: 'Votre commande a été validée et est en cours de préparation',
      progress: 25
    },
    confirmee: { 
      label: 'Prête', 
      subtitle: 'Votre commande est prête pour la livraison',
      progress: 50 
    },
    en_chemin: { 
      label: 'En livraison', 
      subtitle: 'Votre commande est en route vers vous',
      progress: 75
    },
    livree: { 
      label: 'Livrée', 
      subtitle: 'Votre commande a été livrée avec succès',
      progress: 100
    }
  };
  
  // Ordre des statuts pour la progression
  const statusOrder = ['en_cours', 'validee', 'confirmee', 'en_chemin', 'livree'];
  
  // Vérifier si les notifications sont activées pour cette commande
  useEffect(() => {
    const checkNotificationStatus = async () => {
      // Vérifier la permission des notifications
      if (NotificationUtils.isSupported()) {
        setNotificationPermission(Notification.permission);
      }
      
      // Vérifier si cette commande est déjà abonnée
      if (orderId) {
        try {
          const isSubscribed = await NotificationUtils.isSubscribedToOrder(orderId);
          console.log('Statut d\'abonnement pour la commande', orderId, ':', isSubscribed);
          setNotificationsEnabled(isSubscribed);
        } catch (error) {
          console.error('Erreur lors de la vérification du statut des notifications:', error);
        }
      }
    };
    
    checkNotificationStatus();
  }, [orderId]);

  // Charger les détails de la commande
  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId) {
        setError('Identifiant de commande manquant');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        console.log("Chargement de la commande ID:", orderId);
        
        // Vérifier d'abord dans le stockage local
        const savedOrder = getStorageItem(`order_${orderId}`);
        if (savedOrder) {
          console.log("Commande trouvée dans le stockage local");
          try {
            const parsedOrder = JSON.parse(savedOrder);
            setOrder(parsedOrder);
            setCurrentStatus(parsedOrder.statut || 'en_cours');
            setLoading(false);
            
            // Si nous sommes en ligne, tenter quand même de récupérer depuis le serveur
            if (navigator.onLine) {
              try {
                refreshOrderFromServer(orderId);
              } catch (refreshError) {
                console.error("Erreur lors du rafraîchissement depuis le serveur:", refreshError);
              }
            }
            return;
          } catch (parseError) {
            console.error("Erreur parse JSON:", parseError);
          }
        }
        
        // Si nous n'avons pas trouvé la commande en local ou s'il y a eu une erreur, récupérer du serveur
        if (navigator.onLine) {
          await refreshOrderFromServer(orderId);
        } else {
          setError('Impossible de récupérer les détails de la commande en mode hors ligne');
          setLoading(false);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des détails de commande:', error);
        setError('Erreur lors du chargement des détails de commande');
        setLoading(false);
      }
    };
    
    fetchOrderDetails();
  }, [orderId]);
  
  // Envoyer automatiquement le SMS de notification 2 secondes après l'affichage de la page
  /*useEffect(() => {
    const sendOrderSMS = async () => {
      // Vérifier si la commande est chargée
      if (!order || !orderId) {
        console.log('Commande non chargée, attente...');
        return;
      }
      
      // Ne pas envoyer de SMS pour les commandes déjà livrées
      if (currentStatus === 'livree') {
        console.log('Commande déjà livrée, pas de SMS');
        return;
      }
      
      // Vérifier si on a déjà essayé d'envoyer le SMS pour cette commande (localStorage)
      const smsKey = `sms_sent_order_${orderId}`;
      const smsSent = localStorage.getItem(smsKey);
      
      if (smsSent) {
        console.log(`✅ SMS déjà envoyé pour la commande #${orderId}`);
        return;
      }
      
      try {
        console.log(`📨 Envoi du SMS de notification pour la commande #${orderId}...`);
        
        const result = await ApiService.sendOrderNotificationSMS(orderId);
        
        if (result.success) {
          console.log('✅ SMS de notification envoyé avec succès:', result);
          
          // Marquer que le SMS a été envoyé pour cette commande
          localStorage.setItem(smsKey, 'true');
          localStorage.setItem(`sms_sent_time_${orderId}`, new Date().toISOString());
          
          if (result.already_sent) {
            console.log('ℹ️ SMS avait déjà été envoyé précédemment (selon la base de données)');
          } else {
            console.log('✉️ Nouveau SMS envoyé au client');
          }
        } else {
          console.error('❌ Échec de l\'envoi du SMS:', result.message || result.error);
        }
      } catch (error) {
        console.error('❌ Erreur lors de l\'envoi du SMS de notification:', error);
        // Ne pas bloquer l'affichage de la page si l'envoi échoue
      }
    };
    
    // ✅ Attendre 2 secondes après le chargement complet de la commande
    const timeoutId = setTimeout(() => {
      console.log('⏱️ 2 secondes écoulées, déclenchement de l\'envoi SMS...');
      sendOrderSMS();
    }, 2000);
    
    return () => {
      console.log('🧹 Nettoyage du timeout SMS');
      clearTimeout(timeoutId);
    };
  }, [order, orderId, currentStatus]);*/
  
  // Fonction pour récupérer la commande depuis le serveur
  const refreshOrderFromServer = async (id) => {
    try {
      console.log("Récupération de la commande depuis le serveur:", id);
      const orderData = await ApiService.fetchOrderById(id);
      if (orderData && orderData.id_commande) {
        setOrder(orderData);
        setCurrentStatus(orderData.statut || 'en_cours');
        setStorageItem(`order_${id}`, JSON.stringify(orderData));
      }
    } catch (error) {
      console.error("Erreur lors de la récupération de la commande:", error);
      // Ne pas lever d'erreur ici pour conserver les données locales
    } finally {
      setLoading(false);
    }
  };
  
  // Mettre à jour périodiquement le statut de la commande si elle n'est pas terminée
  useEffect(() => {
    if (!order || currentStatus === 'livree' || !navigator.onLine) return;
    
    const intervalId = setInterval(async () => {
      try {
        const updatedOrder = await ApiService.fetchOrderById(orderId);
        if (updatedOrder && updatedOrder.statut !== currentStatus) {
          setOrder(updatedOrder);
          setCurrentStatus(updatedOrder.statut);
          
          // Sauvegarder la commande mise à jour
          setStorageItem(`order_${orderId}`, JSON.stringify(updatedOrder));

          // Envoyer une notification si les notifications sont activées
          if (notificationsEnabled && Notification.permission === 'granted') {
            sendOrderStatusNotification(updatedOrder.statut);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la mise à jour du statut de la commande:', error);
      }
    }, 30000); // Vérifier toutes les 30 secondes
    
    return () => clearInterval(intervalId);
  }, [orderId, order, currentStatus, notificationsEnabled]);
  
  // Fonction pour envoyer une notification de statut
  const sendOrderStatusNotification = (status) => {
    if (!NotificationUtils.isSupported() || Notification.permission !== 'granted') {
      return;
    }
    
    let title, body;
    switch (status) {
      case 'validee':
        title = 'Commande validée';
        body = 'Votre commande a été validée et est en cours de préparation.';
        break;
      case 'confirmee':
        title = 'Commande confirmée';
        body = 'Votre commande est prête à être livrée.';
        break;
      case 'en_chemin':
        title = 'Commande en livraison';
        body = 'Votre commande est en route vers vous !';
        break;
      case 'livree':
        title = 'Commande livrée';
        body = 'Votre commande a été livrée. Merci de votre confiance !';
        break;
      default:
        title = 'Mise à jour de commande';
        body = `Le statut de votre commande a été mis à jour : ${status}`;
    }
    
    NotificationUtils.showNotification({
      title: title,
      body: body,
      icon: './logo.png',
      requireInteraction: true,
      actions: [{
        action: 'view',
        title: 'Voir'
      }],
      data: {
        url: window.location.href,
        orderId: orderId,
        status: status
      }
    });
  };
  
  // Activer les notifications
  const handleEnableNotifications = async () => {
    console.log("Demande d'activation des notifications...");
    
    if (!NotificationUtils.isSupported()) {
      alert('Les notifications ne sont pas prises en charge par ce navigateur');
      return;
    }
    
    try {
      // Demander la permission de notification si elle n'est pas déjà accordée
      if (Notification.permission !== 'granted') {
        console.log("Demande d'autorisation des notifications...");
        const permission = await NotificationUtils.requestPermission();
        setNotificationPermission(permission);
        
        if (permission !== 'granted') {
          alert('Vous devez autoriser les notifications pour recevoir des mises à jour de commande');
          return;
        }
      }
      
      // Si nous sommes ici, les notifications sont autorisées
      console.log("Notifications autorisées, inscription de la commande...");
      
      // S'abonner à la commande en utilisant notre utilitaire
      const subscribed = await NotificationUtils.subscribeToOrderNotifications(orderId);
      console.log("Résultat de l'abonnement:", subscribed);
      
      if (subscribed) {
        // Mettre à jour l'état local pour masquer le bouton d'activation
        setNotificationsEnabled(true);
        
        alert('Notifications activées ! Vous recevrez des mises à jour sur le statut de votre commande.');
      } else {
        alert('Impossible d\'activer les notifications. Veuillez réessayer plus tard.');
      }
    } catch (error) {
      console.error('Erreur lors de l\'activation des notifications:', error);
      alert('Une erreur est survenue lors de l\'activation des notifications.');
    }
  };
  
  // Calculer les statuts pour l'affichage
  const getStatusForDisplay = () => {
    if (!order) return [];
    
    const currentStatusIndex = statusOrder.indexOf(currentStatus);
    
    return statusOrder.map((status, index) => {
      const isCompleted = index < currentStatusIndex;
      const isActive = index === currentStatusIndex;
      
      return {
        key: status,
        title: statusMap[status].label,
        subtitle: statusMap[status].subtitle,
        completed: isCompleted,
        active: isActive
      };
    });
  };
  
  // Calculer la progression
  const getProgressPercentage = () => {
    if (!order || !statusMap[currentStatus]) return 0;
    return statusMap[currentStatus].progress;
  };
  
  // Commander à nouveau
  const handleOrderAgain = () => {
    navigate('/welcome-page');
  };
  
  if (loading) {
    return (
      <div className="order-page">
        <Header title="Suivi de commande" showBackButton={true} onBackClick={() => navigate('/welcome-page')} />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Chargement de votre commande...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="order-page">
        <Header title="Suivi de commande" showBackButton={true} onBackClick={() => navigate('/welcome-page')} />
        <div className="error-container">
          <div className="error-icon">
            <svg viewBox="0 0 24 24" width="64" height="64" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
          </div>
          <h2 className="error-title">Commande introuvable</h2>
          <p className="error-message">{error}</p>
          <button 
            onClick={() => navigate('/welcome-page')}
            className="order-button"
          >
            Passer une nouvelle commande
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="order-page">
      <Header title="Suivi de commande" showBackButton={true} onBackClick={() => navigate('/welcome-page')} />
      
      {/* Bannière de notification */}
      {!notificationsEnabled && notificationPermission !== 'denied' && (
        <div className="notification-banner">
          <span className="notification-text">Activez les notifications pour suivre votre commande</span>
          <button 
            onClick={handleEnableNotifications}
            className="notification-button"
          >
            Activer
          </button>
        </div>
      )}
      
      {/* Informations de la commande */}
      <div className="order-header">
        <div className="order-reference-badge">
          Commande {order?.reference || `#${orderId}`}
        </div>
        <h2 className="order-title">Suivi de votre commande</h2>
        <p className="order-date">
          {order?.date_creation ? new Date(order.date_creation).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : 'Date inconnue'}
        </p>
      </div>
      
      {/* Statistiques */}
      {order && (
        <div className="order-summary-stats">
          <div className="stat-item">
            <span className="stat-label">Articles</span>
            <span className="stat-value">{order.items?.length || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Quantité totale</span>
            <span className="stat-value">
              {order.items?.reduce((sum, item) => sum + item.quantite, 0) || 0}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Mode de paiement</span>
            <span className="stat-value">
              {order.mode_paiement === 'OM' ? 'Orange Money' : 
               order.mode_paiement === 'MoMo' ? 'Mobile Money' :
               order.mode_paiement === 'livraison' ? 'À la livraison' : 
               order.mode_paiement}
            </span>
          </div>
        </div>
      )}
      
      {/* Timeline statuts */}
      <div className="order-status-container">
        <div className="status-timeline">
          <div className="status-line"></div>
          <div 
            className="status-line-progress"
            style={{ height: `${getProgressPercentage()}%` }}
          ></div>
          
          {getStatusForDisplay().map((status) => (
            <div 
              key={status.key} 
              className={`status-item ${status.active ? 'active' : ''} ${status.completed ? 'completed' : ''}`}
            >
              <div className={`status-circle ${status.active ? 'active' : ''} ${status.completed ? 'completed' : ''}`}></div>
              <div className="status-content">
                <div className="status-title">{status.title}</div>
                <div className="status-subtitle">{status.subtitle}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Détails commande */}
      {order && (
        <div className="order-details">
          <h3 className="details-title">Détails de la commande</h3>
          <div className="order-items">
            {order.items && order.items.map((item, index) => (
              <div key={item.id_ligne || index} className="order-item">
                <div className="item-info">
                  <span className="item-name">{item.nom}</span>
                  <span className="item-packaging">{item.packaging}</span>
                  <span className="item-quantity">x{item.quantite}</span>
                </div>
                <span className="item-price">{item.sous_total || (item.prix_unitaire * item.quantite)} F</span>
              </div>
            ))}
          </div>
          
          <div className="order-total">
            <span className="total-label">Total</span>
            <span className="total-amount">{order.montant_total} F</span>
          </div>
        </div>
      )}
      
      {/* Infos livraison */}
      {order && (
        <div className="delivery-info">
          <h3 className="details-title">Informations de livraison</h3>
          
          <div className="info-row">
            <div className="info-label">Nom</div>
            <div className="info-value">
              {order.client?.prenom || order.contact_livraison_nom || "Non spécifié"}
            </div>
          </div>
          
          <div className="info-row">
            <div className="info-label">Téléphone</div>
            <div className="info-value">
              {order.client?.telephone || order.contact_livraison_telephone || "Non spécifié"}
            </div>
          </div>
          
          {(order.client?.adresse_google_places || order.adresse_google_places) && (
            <div className="info-row">
              <div className="info-label">Lieu de livraison</div>
              <div className="info-value">
                <div className="address-google-places">
                  <svg className="location-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  <span>{order.client?.adresse_google_places || order.adresse_google_places}</span>
                </div>
              </div>
            </div>
          )}
          
          <div className="info-row">
            <div className="info-label">Adresse de livraison</div>
            <div className="info-value">
              {order.client?.adresse || order.adresse_livraison || "Non spécifiée"}
            </div>
          </div>
          
          {(order.client?.latitude && order.client?.longitude) && (
            <div className="info-row">
              <div className="info-label">Localisation</div>
              <div className="info-value">
                <a 
                  href={`https://maps.google.com/?q=${order.client.latitude},${order.client.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="map-link primary"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  Voir sur Google Maps
                </a>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Actions */}
      <div className="order-actions">
        <button 
          onClick={handleOrderAgain}
          className="action-button primary"
        >
          Commander à nouveau
        </button>
        
        {!notificationsEnabled && notificationPermission !== 'denied' && (
          <button 
            onClick={handleEnableNotifications}
            className="action-button secondary"
          >
            Activer les notifications
          </button>
        )}
      </div>
    </div>
  );
};

export default OrderPage;